import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../ui/dialog";
import { Progress } from "../ui/progress"
import { Button } from "../ui/button"

import CollapsibleTaskList from "./CollapsibleTaskList";
import Badge from "../generic/Badge";
import FeatureList from "components/generic/FeatureList";

import { Circle, PhoneCall, Mail, Target, Instagram } from "lucide-react";
import { roleOptions, skillOptions } from "data/staticData";

export default function CrewDetailsModal({ open, onOpenChange, crewMember }) {
  const [profileImage, setProfileImage] = useState("/images/misc/profile.png");

  useEffect(() => {
    if (!crewMember) return;

    if (crewMember?.crew_member_files?.length > 0) {
      const image = crewMember.crew_member_files.find(
        file => file.file_type === "profile_photo"
      );

      if (image) {
        if (image.file_path.includes("http")) {
          setProfileImage(image.file_path);
        } else {
          const s3FullUrl = `${process.env.REACT_APP_S3_BASE_URL}/${image.file_path}`;
          setProfileImage(s3FullUrl);
        }
      }
    }
  }, [crewMember]);

  // After hooks, now safely return null
  if (!crewMember) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="p-0" />
      </Dialog>
    );
  }

  const bio = crewMember?.bio || '';
  const name = `${crewMember?.first_name} ${crewMember?.last_name}` || "crewMember Member";
  const availabilityStatus = crewMember?.is_available ? "available" : "busy";
  const profileType = crewMember?.is_beige_member;
  const phone = crewMember?.phone || "";
  const email = crewMember?.email || "";
  const rating = parseInt(crewMember?.rating) || 0;
  const tasks = crewMember?.assigned_tasks || [];

  // Check if bio contains Instagram URL
  const instagramUrlRegex = /(https?:\/\/)?(www\.)?instagram\.com\/[^\s]*/i;
  const instagramMatch = bio.match(instagramUrlRegex);
  const instagramUrl = instagramMatch ? instagramMatch[0] : null;

  // Remove Instagram URL from bio text and trim whitespace
  const role = instagramUrl ? bio.replace(instagramUrlRegex, '').trim() : bio;

  // Determine availability badge properties
  const availabilityBadgeProps = {
    available: {
      text: 'Available',
      textColor: 'text-green-600',
      bgColor: 'bg-green-100 border border-green-300',
    },
    unavailable: {
      text: 'Unavailable',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
    busy: {
      text: 'On Task',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    },
  };
  const availabilityBadge = availabilityBadgeProps[availabilityStatus] || availabilityBadgeProps.available;

  let tasksCompleted, progressPercent = 0
  if (tasks && tasks.length > 0) {
    tasksCompleted = tasks.filter(task => (task.status == "completed" || task.status == "complete" || task.status == "done"))
    progressPercent = (tasksCompleted.length / tasks.length) * 100
  }
  const parseList = (data) => {
    if (!data) return [];

    // Already an array
    if (Array.isArray(data)) return data;

    // Handle string values
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (_) {
        return data
          .split(/[,|]/)
          .map(item => item.trim())
          .filter(Boolean);
      }
    }

    // Fallback
    return [];
  };

  const equipmentList = parseList(crewMember.equipment_ownership);
  const skillsList = parseList(crewMember.skills);
  const mappedSkills = skillOptions
    .filter(skill => skillsList.includes(Number(skill.value)) || skillsList.includes(String(skill.value)))
    .map(skill => skill.label);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Modal Content */}
      <DialogContent
        className="fixed top-0 right-0 h-full w-full md:w-[520px] max-w-none rounded-none p-0 overflow-hidden right-drawer-override"
      >
        {/* START: Main Padding Wrapper */}
        <div className="p-6 h-full flex flex-col overflow-y-auto pb-[140px]">

          {/* Header */}
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-semibold">
              Crew Member Details
            </DialogTitle>
          </DialogHeader>

          {/* Profile Section - FIXED LAYOUT */}
          <div className="flex flex-wrap justify-between px-4 gap-4">
            <div className="flex items-start justify-start gap-2">
              <img
                src={profileImage}
                alt={name}
                className="w-[72px] h-[72px] rounded-lg object-cover"
              />
              <div className="flex flex-col gap-1">
                <p className="text-base text-foreground font-semibold">{name}</p>
                <p className="text-muted-foreground text-sm">{role}</p>
                <div className="flex gap-2 justify-start">
                  {
                    (profileType == 1) ? (
                      <Badge
                        icon={<Target className="w-4 h-4 text-orange-600" />}
                        text={'Beige'}
                        textColor={"text-orange-600"}
                        bgColor={"bg-orange-50 dark:bg-orange-900/20"}
                      />) : (
                      <Badge
                        icon={<Target className="w-4 h-4 text-blue-800" />}
                        alt={"Profile Type"}
                        text={"Memehouse"}
                        textColor={"text-blue-800"}
                        bgColor={"bg-blue-50"}
                      />
                    )
                  }
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-between items-end">
              <div className="flex gap-2">
                <Badge
                  icon={<Circle className={`w-4 h-4 ${availabilityBadge.textColor}`} />}
                  text={availabilityBadge.text}
                  textColor={availabilityBadge.textColor}
                  bgColor={availabilityBadge.bgColor}
                />
                {instagramUrl && (
                  <a
                    href={instagramUrl.startsWith('http') ? instagramUrl : `https://${instagramUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-full bg-pink-50 hover:bg-pink-100 dark:bg-pink-900/20 dark:hover:bg-pink-900/30 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Instagram className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                  </a>
                )}
              </div>

              {rating > 0 && (
                <p className="inline-flex gap-1 text-muted-foreground text-sm">
                  ⭐
                  {rating.toFixed(1)}
                </p>
              )}
            </div>

          </div>

          {(phone || email) && (
            <div className="flex gap-2 m-4">
              {phone && (
                <Badge
                  icon={<PhoneCall className="w-4 h-4 text-dark-blue dark:text-indigo-400" />}
                  text={phone}
                  textColor={"text-dark-blue dark:text-indigo-400"}
                  bgColor={"bg-indigo-50 dark:bg-indigo-900/20"}
                />
              )}
              {email && (
                <Badge
                  icon={<Mail className="w-4 h-4 text-dark-blue dark:text-indigo-400" />}
                  text={email}
                  textColor={"text-dark-blue dark:text-indigo-400"}
                  bgColor={"bg-indigo-50 dark:bg-indigo-900/20"}
                />
              )}
            </div>
          )}

          {/* Task Progress */}
          {
            (tasks && tasks.length > 0) &&
            (
              <>
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex justify-between">
                    <p className="text-sm font-semibold text-foreground">Task Progress</p>
                    <p className="text-sm font-semibold">{`${tasksCompleted.length}/${tasks.length} completed`}</p>
                  </div>
                  <Progress
                    value={progressPercent}
                    className={"w-full h-2 rounded-full bg-indigo-600/20 [&>div]:bg-dark-blue [&>div]:rounded-full"}
                  />
                </div>
                <hr className="border-border" />
                <CollapsibleTaskList tasks={tasks} bg={"bg-background"} />
              </>
            )
          }

          {/* Equipments */}
          <hr className="mb-4 border-border" /> {/* Moved hr up */}
          <div className="flex flex-col w-full">
            {
              equipmentList.length > 0 &&
              <FeatureList
                title={"Equipment"}
                list={equipmentList}
                visibleCount={4}
              />
            }
            {
              mappedSkills.length > 0 &&
              <FeatureList
                title={"Skills"}
                list={mappedSkills}
                visibleCount={4}
              />
            }
          </div>
        </div>
        <DialogFooter className="absolute bottom-0 left-0 w-full border-t bg-white p-4 flex flex-col gap-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">

          <Button asChild className="w-full">
            <Link
              to="/crew-management/assign-task"
              state={{ crewMember }}
              className="flex justify-center items-center w-full"
            >
              Assign Task
            </Link>
          </Button>

          <DialogClose asChild>
            <Button variant="outline" className="w-full">
              Cancel
            </Button>
          </DialogClose>

        </DialogFooter>
        <DialogClose />
      </DialogContent>
    </Dialog >
  );
}
